import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { BarChart3, Settings, HelpCircle, LogOut, User, Building2, Shield } from 'lucide-react';
import { getAlertStats } from "../../utils/helper";
import { useAuth, ROLES } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';

const navItems = [
  { path: '/', labelKey: 'nav.dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', permission: 'view_dashboard' },
  { path: '/loans', labelKey: 'nav.loans', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', permission: 'view_loans' },
  { path: '/waivers', labelKey: 'nav.waivers', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', permission: 'view_loans', badge: 'new' },
  { path: '/esg', labelKey: 'nav.esg', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z', permission: 'view_dashboard' },
  { path: '/alerts', labelKey: 'nav.alerts', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', permission: 'view_alerts' },
  { path: '/import', labelKey: 'nav.import', icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12', permission: 'import_data' },
  { path: '/reports', labelKey: 'nav.reports', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', permission: 'view_reports' },
];

export default function Sidebar() {
  const { user, logout, hasPermission, hasRole } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alertStats = getAlertStats();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = () => {
    if (!user) return '??';
    return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getRoleBadgeColor = () => {
    switch (user?.role) {
      case ROLES.AGENT:
        return 'bg-purple-500';
      case ROLES.LENDER:
        return 'bg-blue-500';
      case ROLES.BORROWER:
        return 'bg-green-500';
      default:
        return 'bg-slate-500';
    }
  };

  const getRoleLabel = () => {
    switch (user?.role) {
      case ROLES.AGENT:
        return t('roles.agent') || 'Facility Agent';
      case ROLES.LENDER:
        return t('roles.lender') || 'Lender';
      case ROLES.BORROWER:
        return t('roles.borrower') || 'Borrower';
      default:
        return user?.role || 'User';
    }
  };


  const filteredNavItems = navItems.filter(
    (item) => !item.permission || hasPermission(item.permission)
  );

  return (
    <aside className="w-64 bg-slate-900 text-white h-screen flex flex-col">
      {}
      <div className="p-6 border-b border-slate-700">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
            <BarChart3 size={18} />
          </div>
          Covenant Tracker
        </h1>
        <p className="text-slate-400 text-xs mt-1">Stay one step ahead of the violation</p>
      </div>

      {}
      <nav className="flex-1 overflow-auto p-4 space-y-1">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 mb-2">
          {t('nav.mainMenu')}
        </p>
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800'
              }`
            }
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
              </svg>
              {t(item.labelKey)}
            </div>
            {item.path === '/alerts' && alertStats.unread > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                {alertStats.unread}
              </span>
            )}
            {item.badge === 'new' && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-green-500 text-white rounded-full">
                NEW
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {}
      <div className="p-4 border-t border-slate-700">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 mb-2">
          {t('nav.settings')}
        </p>
        {hasPermission('manage_settings') && (
          <NavLink
            to="/company"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800'
              }`
            }
          >
            <Building2 size={20} />
            {t('nav.company')}
          </NavLink>
        )}
        {hasRole(ROLES.AGENT) && (
          <NavLink
            to="/roles"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800'
              }`
            }
          >
            <Shield size={20} />
            {t('nav.roles')}
          </NavLink>
        )}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
              isActive
                ? 'bg-blue-600 text-white'
                : 'text-slate-300 hover:bg-slate-800'
            }`
          }
        >
          <Settings size={20} />
          {t('nav.settings')}
        </NavLink>
        <NavLink
          to="/help"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
              isActive
                ? 'bg-blue-600 text-white'
                : 'text-slate-300 hover:bg-slate-800'
            }`
          }
        >
          <HelpCircle size={20} />
          {t('nav.help')}
        </NavLink>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
        >
          <LogOut size={20} />
          {t('nav.logout')}
        </button>
      </div>
    </aside>
  );
}
